import { FilterDescriptor, State, process } from '@progress/kendo-data-query';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import {
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridPagerSettings,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { AxiosResponse } from 'axios';
import { Dispatch, FC, SetStateAction, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useGLCodeCategories } from 'src/assets/static/view_department/useDepartmentCategories';
import { useGLCodeColumn } from 'src/assets/static/view_department/useGLCodeColumn';
import GridWrapper from 'src/components/GridWrapper';
import useViewDepartment from 'src/hooks/useViewDepartments';
import useWindowSize from 'src/hooks/useWindowSize';
import { GlCodeResponse } from 'src/types/GLCode';
import { BulkUpdateGLCodeRequest, Department } from 'src/types/department';
import { modifyFilters } from 'src/utils/requestInterceptor';
import { initialDepartmentState } from '../ViewDepartmentContext';
import { viewDepartmentFilters } from '../ViewDepartmentFilters';
import BulkUpdateTableToolbar from './BulkUpdateTableToolbar';

const selectable: GridSelectableSettings = {
  enabled: false,
  drag: false,
  cell: false,
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 1,
  pageSizes: [10, 20, 50, 100, 5000],
};

const defaultProps = {
  tableTextId: 'GL Codes',
  DataItemKey: 'rowId',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  pageable: gridPagerSettings,
} as const;

const initialAssignGLCodeDataState: State = {
  skip: 0,
  take: 5000,
  sort: [],
  filter: {
    logic: 'and',
    filters: [],
  },
};
interface AssignGLCodeTableProps {
  handlePaneClose: () => void;
  selectedDepartments: Department[];
  assignGLCode: GlCodeResponse[];
  setAssignGLCode: Dispatch<SetStateAction<GlCodeResponse[]>>;
  bulkUpdateGLCode: (params: BulkUpdateGLCodeRequest) => Promise<AxiosResponse<any, any>>;
  isBulkUpdateGLCodeLoading: boolean;
}

const AssignGLCodeTable: FC<AssignGLCodeTableProps> = ({
  handlePaneClose,
  selectedDepartments,
  assignGLCode,
  setAssignGLCode,
  bulkUpdateGLCode,
  isBulkUpdateGLCodeLoading,
}) => {
  const { height } = useWindowSize();
  const dropDownList = useGLCodeCategories();
  const { keepSelectedRows, t, setDepartmentsDataState } = useViewDepartment();

  const assignGLCodeColumns = useGLCodeColumn();

  const [state, setState] = useState(initialAssignGLCodeDataState);
  const [selectionFilter, setSelectionFilter] = useState(dropDownList[1]);

  const selectedRows = useMemo(
    () => assignGLCode.filter((glCode) => glCode.selected),
    [assignGLCode]
  );

  const getAssignGLCodeFilters = useCallback(
    (state: State) => {
      const filters: FilterDescriptor[] = modifyFilters(state.filter, undefined, false);
      return [...filters, ...(viewDepartmentFilters[selectionFilter.value]?.filters || [])];
    },
    [selectionFilter.value]
  );

  const { data: processedGLCodes, total } = useMemo(
    () =>
      process(assignGLCode, {
        ...state,
        filter: {
          logic: 'and',
          filters: getAssignGLCodeFilters(state),
        },
      }) as {
        data: GlCodeResponse[];
        total: number;
      },
    [getAssignGLCodeFilters, assignGLCode, state]
  );

  const onCheckbookSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      setAssignGLCode((prev) => {
        const prevItems = [...prev].map((item) =>
          item.rowId === event?.dataItem.rowId ? { ...item, selected: !item.selected } : item
        );
        return prevItems;
      });
    },
    [setAssignGLCode]
  );

  const changeDataState = useCallback(
    (e: GridDataStateChangeEvent) => {
      setAssignGLCode((prev) => prev?.map((row) => ({ ...row, selected: false })));
      setState(e.dataState);
    },
    [setAssignGLCode]
  );

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event?.syntheticEvent?.target;
      const { checked } = checkboxElement;
      setAssignGLCode((prev) => prev?.map((row) => ({ ...row, selected: checked })));
    },
    [setAssignGLCode]
  );

  const handleClearSelection = useCallback(() => {
    setAssignGLCode((prev) => prev?.map((row) => ({ ...row, selected: false })));
  }, [setAssignGLCode]);

  const handleFilterChange = useCallback(
    (e: DropDownListChangeEvent) => {
      setAssignGLCode((prev) => prev?.map((row) => ({ ...row, selected: false })));
      setSelectionFilter(e.value);
    },
    [setAssignGLCode]
  );

  const handleAssignGLCode = useCallback(async () => {
    const departments = selectedDepartments.map((department) => department.departmentId);
    const glCodes = processedGLCodes.filter((glCode) => glCode.selected).map((glCode) => glCode.id);

    try {
      await bulkUpdateGLCode({ departments, glCodes, assign: true });
      toast.success(t('GL Codes added to departments successfully'));
      keepSelectedRows();
      handlePaneClose();
      setDepartmentsDataState(initialDepartmentState);
    } catch (error) {
      toast.error(t('Failed to add GL Codes to departments'));
    }
  }, [
    bulkUpdateGLCode,
    handlePaneClose,
    processedGLCodes,
    keepSelectedRows,
    selectedDepartments,
    setDepartmentsDataState,
    t,
  ]);

  const gridToolbarChildren = useMemo(
    () => (
      <BulkUpdateTableToolbar
        isAssign={true}
        isMember={false}
        selectionFilter={selectionFilter}
        dropDownList={dropDownList}
        handleClearSelection={handleClearSelection}
        selectedRows={selectedRows}
        handleFilterChange={handleFilterChange}
        handleUpdate={handleAssignGLCode}
        selectedDepartment={selectedDepartments}
        isLoading={isBulkUpdateGLCodeLoading}
      />
    ),
    [
      dropDownList,
      handleAssignGLCode,
      handleClearSelection,
      handleFilterChange,
      isBulkUpdateGLCodeLoading,
      selectedDepartments,
      selectedRows,
      selectionFilter,
    ]
  );

  return (
    <main className="p-[10px] bg-[#ffffff]">
      <GridWrapper
        id="assignGLCodeTable"
        columns={assignGLCodeColumns}
        dataSource={processedGLCodes}
        dataState={state}
        total={total}
        height={`${height - 320}px`}
        gridToolbarChildren={gridToolbarChildren}
        onDataStateChange={changeDataState}
        {...defaultProps}
        onHeaderSelectionChange={onHeaderSelectionChange}
        onSelectionChange={onCheckbookSelectionChange}
        noRecordText={t('No records to display')}
        loading={isBulkUpdateGLCodeLoading}
        hideScrollBar
      />
    </main>
  );
};

export default AssignGLCodeTable;
